'use client';

import { create } from 'zustand';
import type { LoginResponse } from '@/types/auth.types';

/**
 * How long a mutation may sit waiting for the user to re-authenticate before it
 * gives up. Without a ceiling, an overlay someone walked away from would hold
 * requests open indefinitely.
 */
const REAUTH_TIMEOUT_MS = Number(process.env.NEXT_PUBLIC_REAUTH_TIMEOUT_MS ?? 600_000);

/** Transient failures: retry, never log out. */
const GENERIC_BACKOFF_MS = [500, 2000, 5000];
/**
 * A Redis restart is measured in tens of seconds, not hundreds of milliseconds.
 * ~63s of budget so the session outlives the outage instead of being destroyed
 * by it.
 */
const STORE_UNAVAILABLE_BACKOFF_MS = [1000, 2000, 5000, 10_000, 15_000, 30_000];

const AUTH_BROADCAST_CHANNEL = 'eifo-auth';

/** Kill switch for the whole work-preserving re-auth path (D26). */
export function reauthOverlayEnabled(): boolean {
  return process.env.NEXT_PUBLIC_REAUTH_OVERLAY_ENABLED !== 'false';
}

/**
 * Whether this tab's session belongs to a human.
 *
 * `unattended` is the LiveKit egress recorder, which pushes its own short-lived
 * JWT into this very store. Shared client code must never prompt it for
 * credentials — a login form rendered there is captured into a class recording.
 */
export type SessionKind = 'interactive' | 'unattended';

interface AuthState {
  accessToken: string | null;
  user: LoginResponse['user'] | null;
  profileId: string | null;
  activeChildName: string | null;
  activeChildGradeLevel: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True once restoreSession() has completed (regardless of outcome). The store
   * hydrates asynchronously — SessionHydrator runs restoreSession() in a passive
   * useEffect AFTER first paint — so on a hard load a real parent has a painted
   * frame where `isLoading===false && user===null`. Neither `isLoading` nor
   * `user !== undefined` can distinguish "not-yet-restored" from
   * "restored-anonymous"; this flag is the ONLY reliable "auth resolved" signal.
   */
  hasHydrated: boolean;
  /**
   * The session ended and the user must sign in again — WITHOUT unmounting the
   * React tree. Deliberately separate from `isAuthenticated`, because
   * `AuthProvider` renders a spinner in place of `children` when that goes
   * false, which would destroy the very work this flag exists to preserve.
   */
  sessionExpired: boolean;
  /** When the current access token was minted — drives proactive refresh. */
  tokenIssuedAt: number | null;
  sessionKind: SessionKind;
  /**
   * The profile that was active when the session expired. `/api/auth/login`
   * returns a token with no `profileId`, so without this a parent working in
   * child view is silently thrown back to parent context on re-auth.
   */
  priorProfileId: string | null;
  /** A refresh returned a different role than the one held in this tab. */
  roleChanged: boolean;
  setAuth: (accessToken: string, user: LoginResponse['user']) => void;
  setProfile: (profileId: string | null, activeChildName: string | null, gradeLevel?: string | null) => void;
  setLanguage: (language: string) => void;
  setTimezone: (timezone: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  setSessionExpired: (expired: boolean) => void;
  setSessionKind: (kind: SessionKind) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  profileId: null,
  activeChildName: null,
  activeChildGradeLevel: null,
  isAuthenticated: false,
  isLoading: false,
  hasHydrated: false,
  sessionExpired: false,
  tokenIssuedAt: null,
  sessionKind: 'interactive',
  priorProfileId: null,
  roleChanged: false,
  setAuth: (accessToken, user) =>
    set({
      accessToken,
      user,
      isAuthenticated: true,
      isLoading: false,
      tokenIssuedAt: Date.now(),
    }),
  setProfile: (profileId, activeChildName, gradeLevel) =>
    set({ profileId, activeChildName, activeChildGradeLevel: gradeLevel ?? null }),
  setLanguage: (language) =>
    set((state) => ({
      user: state.user ? { ...state.user, preferredLanguage: language } : null,
    })),
  // Keep the cached user's timezone in sync after a profile save. Times shown in
  // the enrollment catalog are projected client-side from this value
  // (useViewerTimezone reads user.timezone), so without this the display stays
  // stale until the next token refresh / re-login re-runs setAuth.
  setTimezone: (timezone) =>
    set((state) => ({
      user: state.user ? { ...state.user, timezone } : null,
    })),
  clearAuth: () =>
    set({
      accessToken: null,
      user: null,
      profileId: null,
      activeChildName: null,
      activeChildGradeLevel: null,
      isAuthenticated: false,
      isLoading: false,
      tokenIssuedAt: null,
      sessionExpired: false,
      priorProfileId: null,
      roleChanged: false,
    }),
  setLoading: (loading) => set({ isLoading: loading }),
  setSessionExpired: (expired) => set({ sessionExpired: expired }),
  setSessionKind: (kind) => set({ sessionKind: kind }),
}));

// --- Auth operations ---

export async function loginUser(
  email: string,
  password: string
): Promise<LoginResponse> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new AuthError(
      data.error?.message ?? 'Unable to log in. Please try again later.',
      data.error?.code ?? 'UNKNOWN_ERROR'
    );
  }

  useAuthStore.getState().setAuth(data.accessToken, data.user);

  return data;
}

export async function logoutUser(reason?: string): Promise<void> {
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } catch {
    // Ignore network errors during logout
  }

  useAuthStore.getState().clearAuth();
  unregisterUnloadGuard();
  // Both of these are module-level set-once latches. They were safe only while
  // every logout reloaded the page and reset the module; in-place re-auth means
  // the module now survives, so they must be cleared explicitly (D8).
  resetRestoreLatch();

  const url = reason ? `/login?reason=${reason}` : '/login';
  if (typeof window !== 'undefined') {
    window.location.href = url;
  }
}

// ─── Re-auth handshake ────────────────────────────────────────────────────
//
// A request that hit an expired session parks on `reauthPromise` until the
// overlay reports back. That promise is the mechanism by which a save resumes
// after sign-in rather than being lost.

let reauthPromise: Promise<string | null> | null = null;
let resolveReauth: ((token: string | null) => void) | null = null;
let reauthTimeout: ReturnType<typeof setTimeout> | null = null;

function awaitReauth(): Promise<string | null> {
  if (!reauthPromise) {
    reauthPromise = new Promise<string | null>((resolve) => {
      resolveReauth = resolve;
    });
    reauthTimeout = setTimeout(() => completeReauth(null), REAUTH_TIMEOUT_MS);
  }
  return reauthPromise;
}

/**
 * Called by the re-auth overlay: pass the new access token to release every
 * waiting request, or `null` to fail them (sign-out, or the user walked away).
 */
export function completeReauth(token: string | null): void {
  if (reauthTimeout) {
    clearTimeout(reauthTimeout);
    reauthTimeout = null;
  }
  const resolve = resolveReauth;
  reauthPromise = null;
  resolveReauth = null;
  resolve?.(token);

  if (token) {
    unregisterUnloadGuard();
    broadcastReauthSuccess();
    console.info('[auth] reauth_succeeded');
  }
}

// ─── Unload guard while expired ───────────────────────────────────────────

function expiredUnloadHandler(event: BeforeUnloadEvent): void {
  event.preventDefault();
  event.returnValue = '';
}

let unloadGuardRegistered = false;

function registerUnloadGuard(): void {
  if (typeof window === 'undefined' || unloadGuardRegistered) return;
  window.addEventListener('beforeunload', expiredUnloadHandler);
  unloadGuardRegistered = true;
}

function unregisterUnloadGuard(): void {
  if (typeof window === 'undefined' || !unloadGuardRegistered) return;
  window.removeEventListener('beforeunload', expiredUnloadHandler);
  unloadGuardRegistered = false;
}

// ─── Cross-tab dismissal ──────────────────────────────────────────────────
//
// The store is per-tab and in-memory, so without a channel an admin with three
// tabs open signs in three times for one expiry.

let authChannel: BroadcastChannel | null = null;

function getAuthChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!authChannel) {
    authChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    authChannel.onmessage = (event: MessageEvent) => {
      if (event.data?.type !== 'reauth-success') return;
      // A sibling tab re-authenticated. The refresh cookie is shared, so this
      // tab can simply resume — it does not need its own sign-in.
      if (useAuthStore.getState().sessionExpired) {
        useAuthStore.setState({ sessionExpired: false, roleChanged: false });
        unregisterUnloadGuard();
        void refreshAccessToken().then((token) => {
          if (token) completeReauth(token);
        });
      }
    };
  }
  return authChannel;
}

function broadcastReauthSuccess(): void {
  try {
    getAuthChannel()?.postMessage({ type: 'reauth-success' });
  } catch {
    // Feature-detected best effort — never block re-auth on the channel.
  }
}

/** Subscribe this tab to sibling re-auth events. Safe to call repeatedly. */
export function initAuthBroadcast(): void {
  getAuthChannel();
}

// ─── Refresh ──────────────────────────────────────────────────────────────

type RefreshFailure =
  | 'expired' // 401 / SESSION_MAX_AGE — a real end of session
  | 'account_disabled' // 403 — hard logout, no overlay
  | 'exhausted'; // transient, but we ran out of retry budget

type RefreshResult =
  | { ok: true; accessToken: string }
  | { ok: false; failure: RefreshFailure };

/**
 * Shared in-flight refresh (D2). One promise serves BOTH `restoreSession` and
 * `refreshAccessToken`, so a page-load restore and a 401-driven refresh can
 * never rotate the same cookie concurrently.
 *
 * Cleared on settle — the previous `restorePromise` was set once and never
 * reset, which only worked because logout reloaded the module.
 */
let inFlightRefresh: Promise<RefreshResult> | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function parseRetryAfterMs(response: Response): number | null {
  const header = response.headers?.get?.('Retry-After');
  if (!header) return null;
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : null;
}

async function readErrorCode(response: Response): Promise<string | null> {
  try {
    const data = await response.clone().json();
    return data?.error?.code ?? null;
  } catch {
    return null;
  }
}

/**
 * Perform the refresh request, retrying transient failures.
 *
 * Only 401/403 ends a session. The old code treated ANY non-OK response as
 * fatal and navigated straight to /login, so a 429 from a shared office IP or a
 * momentary Redis blip logged everybody out.
 */
async function performRefresh(): Promise<RefreshResult> {
  let genericAttempt = 0;
  let storeAttempt = 0;

  for (;;) {
    let response: Response;
    try {
      response = await fetch('/api/auth/refresh', { method: 'POST' });
    } catch {
      // Network error — transient by definition.
      if (genericAttempt >= GENERIC_BACKOFF_MS.length) {
        return { ok: false, failure: 'exhausted' };
      }
      await sleep(GENERIC_BACKOFF_MS[genericAttempt++]);
      continue;
    }

    if (response.ok) {
      const data = await response.json();
      const store = useAuthStore.getState();
      const user = data.user ?? store.user;

      // A refresh returns the DB-current role. Silently swapping it under an
      // open admin-only modal would leave the UI showing controls the user no
      // longer has; make it an explicit, visible event instead (D25).
      // Skipped in child view: there `role` reflects which profile is being
      // viewed, not the account's privilege level.
      const isChildView = data.profileId != null || store.profileId != null;
      if (
        !isChildView &&
        store.user &&
        user &&
        store.user.role !== user.role
      ) {
        useAuthStore.setState({ roleChanged: true });
        return { ok: false, failure: 'expired' };
      }

      if (user) {
        store.setAuth(data.accessToken, user);
      }
      // C4: the refresh route always returns the child-view fields, so a
      // page-load restore recovers child view. Guarded on the key being present
      // so a response shape without them can never silently clear the profile.
      if (data && 'profileId' in data) {
        store.setProfile(
          data.profileId ?? null,
          data.activeChildName ?? null,
          data.gradeLevel ?? null
        );
      }
      return { ok: true, accessToken: data.accessToken };
    }

    const code = await readErrorCode(response);

    if (response.status === 403 && code === 'ACCOUNT_DISABLED') {
      return { ok: false, failure: 'account_disabled' };
    }
    if (response.status === 401 || code === 'SESSION_MAX_AGE') {
      return { ok: false, failure: 'expired' };
    }

    // Everything else is transient: 429, 5xx, and the 503 the route returns
    // when the session store itself is unreachable.
    const isStoreOutage =
      response.status === 503 || code === 'SESSION_STORE_UNAVAILABLE';
    const schedule = isStoreOutage ? STORE_UNAVAILABLE_BACKOFF_MS : GENERIC_BACKOFF_MS;
    const attempt = isStoreOutage ? storeAttempt++ : genericAttempt++;

    if (attempt >= schedule.length) {
      return { ok: false, failure: 'exhausted' };
    }

    // A server that tells us when to come back always wins over our schedule.
    const retryAfterMs = parseRetryAfterMs(response);
    await sleep(retryAfterMs ?? schedule[attempt]);
  }
}

function runSharedRefresh(): Promise<RefreshResult> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = performRefresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

/**
 * Enter the expiry path: surface the re-auth overlay and decide whether this
 * caller waits for it.
 */
async function enterExpiryPath(waitForReauth: boolean): Promise<string | null> {
  const store = useAuthStore.getState();

  // The egress recorder must fail silently rather than prompt. Its token lands
  // in this same store, so without this check a login form would be painted
  // into a live class recording.
  if (store.sessionKind === 'unattended') {
    return null;
  }

  // Kill switch (D26/AC40): with the overlay disabled, fall back to today's
  // hard logout. Worse for the user, but known — and a lever that is not
  // "redeploy" matters on the highest-blast-radius surface in the platform.
  if (!reauthOverlayEnabled()) {
    await logoutUser('session-expired');
    return null;
  }

  useAuthStore.setState({
    sessionExpired: true,
    priorProfileId: store.profileId,
  });
  registerUnloadGuard();
  initAuthBroadcast();
  // This bug persisted for months because the only signal was users saying
  // "we got logged out". Count the overlay instead of guessing.
  console.warn('[auth] reauth_overlay_shown');

  if (!waitForReauth) return null;
  return awaitReauth();
}

export interface RefreshOptions {
  /**
   * Whether this caller should park on the re-auth overlay when the session has
   * expired. TRUE for mutations (they carry user work), FALSE for queries (they
   * refetch on their own once the session is back).
   */
  waitForReauth?: boolean;
}

export async function refreshAccessToken(
  options?: RefreshOptions
): Promise<string | null> {
  const waitForReauth = options?.waitForReauth ?? false;

  // Only an ESTABLISHED session may reach the expiry path (D2). SessionHydrator
  // lives in the root layout and calls restoreSession() on every page load, so
  // for a visitor with no cookie the refresh legitimately 401s — surfacing the
  // overlay there would turn a cold page load into a bootleg login form.
  const before = useAuthStore.getState();
  const hasEstablishedSession =
    before.isAuthenticated || before.tokenIssuedAt !== null;

  const result = await runSharedRefresh();

  if (result.ok) return result.accessToken;

  if (result.failure === 'account_disabled') {
    await logoutUser('account-disabled');
    return null;
  }

  if (!hasEstablishedSession) return null;

  return enterExpiryPath(waitForReauth);
}

/**
 * Restore session from refresh token cookie on page load.
 * Called by AuthProvider and SessionHydrator on mount.
 * Returns true if session was restored, false otherwise.
 */
export async function restoreSession(): Promise<boolean> {
  const store = useAuthStore.getState();
  // Already authenticated (e.g. just logged in) — session is resolved.
  if (store.isAuthenticated) {
    useAuthStore.setState({ hasHydrated: true });
    return true;
  }

  store.setLoading(true);

  try {
    const result = await runSharedRefresh();

    if (!result.ok) {
      // A cold restore failure is the normal case for a signed-out visitor. It
      // is NOT an expiry event — no overlay, no unload guard.
      store.setLoading(false);
      return false;
    }

    // setAuth and the child-view profile fields were applied by the shared
    // refresh, which owns the single response both callers share.
    return true;
  } catch {
    store.setLoading(false);
    return false;
  } finally {
    useAuthStore.setState({ hasHydrated: true });
  }
}

/**
 * Module-level latches that must be cleared when a session ends, now that
 * in-place re-auth means the module is no longer reloaded.
 */
let restoreLatchListeners: Array<() => void> = [];

export function onRestoreLatchReset(listener: () => void): () => void {
  restoreLatchListeners.push(listener);
  return () => {
    restoreLatchListeners = restoreLatchListeners.filter((l) => l !== listener);
  };
}

function resetRestoreLatch(): void {
  inFlightRefresh = null;
  for (const listener of restoreLatchListeners) listener();
}

export class AuthError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = 'AuthError';
  }
}
